import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Paper,
  useMediaQuery,
  Stack,
  Chip,
  Divider,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { fetchProducts, deleteProduct } from "../api/productApi";
import { useTheme } from "@mui/material/styles";

interface Product {
  id: string;
  name: string;
  description: string;
  brand: string;
  status: "active" | "inactive";
  category: string;
  base_price: number;
  image: string;
  master_sku: string;
  stock_quantity: number;
  variants: Array<{
    id: number;
    variant_name: string;
    price: number;
    stock_quantity: number;
    sku: string;
    image: string;
    selected_options: Record<string, string>;
  }>;
}

interface SearchFilterBarProps {
  onSearch: (searchTerm: string) => void;
  onFilter: (filter: { category: string; brand: string }) => void;
  categories: string[];
  brands: string[];
}

const SearchFilterBar: React.FC<SearchFilterBarProps> = ({
  onSearch,
  onFilter,
  categories,
  brands,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    category: "",
    brand: "",
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    onSearch(value);
  };

  const handleFilterChange = (e: any) => {
    const { name, value } = e.target;
    const newFilters = {
      ...filters,
      [name]: value,
    };
    setFilters(newFilters);
    onFilter(newFilters);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: 2,
        mb: 3,
        alignItems: { xs: "stretch", sm: "flex-end" },
      }}
    >
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search products..."
        value={searchTerm}
        onChange={handleSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ 
          maxWidth: { sm: 400 },
          '& .MuiInputBase-root': {
            height: '56px' // Match the height of the Select components
          }
        }}
      />

      <FormControl 
        sx={{ 
          minWidth: 120,
          '& .MuiInputLabel-root': {
            position: 'absolute',
            top: '-8px',
            left: '14px',
            backgroundColor: 'background.paper',
            px: 1
          }
        }} 
        size="small"
      >
        <InputLabel shrink>Category</InputLabel>
        <Select
          native
          value={filters.category}
          onChange={handleFilterChange}
          label="Category"
          name="category"
          notched
        >
          <option value="">All Categories</option>
          {categories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </Select>
      </FormControl>

      <FormControl 
        sx={{ 
          minWidth: 120,
          '& .MuiInputLabel-root': {
            position: 'absolute',
            top: '-8px',
            left: '14px',
            backgroundColor: 'background.paper',
            px: 1
          }
        }} 
        size="small"
      >
        <InputLabel shrink>Brand</InputLabel>
        <Select
          native
          value={filters.brand}
          onChange={handleFilterChange}
          label="Brand"
          name="brand"
          notched
        >
          <option value="">All Brands</option>
          {brands.map((brand) => (
            <option key={brand} value={brand}>
              {brand}
            </option>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

const ProductManagementPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"active" | "inactive">("active");
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    category: "",
    brand: "",
  });
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const data = await fetchProducts(activeTab); // Fetch by status
        const normalizedData = data.map((p: Product) => ({
          ...p,
          category: p.category || "Uncategorized",
        }));
        setProducts(normalizedData);
      } catch (error) {
        console.error("Error loading products:", error);
      }
    };
  
    loadProducts();
  }, [activeTab]); 
  
  useEffect(() => {
    applyFilters();
  }, [products, activeTab, searchTerm, filters]);

  const applyFilters = () => {
    let result = products.filter((p) => p.status === activeTab);

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(term) ||
          p.description.toLowerCase().includes(term) ||
          p.master_sku.toLowerCase().includes(term) ||
          p.variants.some((v) => v.sku.toLowerCase().includes(term))
      );
    }

    if (filters.category) {
      result = result.filter((p) => p.category === filters.category);
    }

    if (filters.brand) {
      result = result.filter((p) => p.brand === filters.brand);
    }

    setFilteredProducts(result);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const handleFilter = (newFilters: { category: string; brand: string }) => {
    setFilters(newFilters);
  };

  const getUniqueCategories = () => {
    const categories = new Set<string>();
    products.forEach((p) => categories.add(p.category || "Uncategorized"));
    return Array.from(categories).sort();
  };

  const getUniqueBrands = () => {
    const brands = new Set<string>();
    products.forEach((p) => p.brand && brands.add(p.brand));
    return Array.from(brands).sort();
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, productId: string) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedProduct(productId);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setSelectedProduct(null);
  };

  const handleDeleteProduct = async () => {
    if (selectedProduct) {
      try {
        await deleteProduct(selectedProduct);
        setProducts(products.filter((product) => product.id !== selectedProduct));
        handleCloseMenu();
      } catch (error) {
        console.error("Failed to delete product:", error);
      }
    }
  };

  const handleEditProduct = () => {
    if (selectedProduct) {
      navigate(`/dashboard/products/edit/${selectedProduct}`);
      handleCloseMenu();
    }
  };

  const calculateTotalStock = (product: Product) => {
    if (product.variants.length > 0) {
      return product.variants.reduce((sum, variant) => sum + variant.stock_quantity, 0);
    }
    return product.stock_quantity;
  };

  const renderMobileProduct = (product: Product) => (
    <Paper key={product.id} sx={{ mb: 2, overflow: "hidden" }}>
      <Box
        display="flex"
        alignItems="center"
        p={2}
        onClick={() => navigate(`/dashboard/products/edit/${product.id}`)}
        sx={{ cursor: "pointer" }}
      >
        <Avatar
          src={product.image}
          alt={product.name}
          variant="square"
          sx={{ width: 56, height: 56, borderRadius: 1 }}
        />
        <Box flexGrow={1} ml={2}>
          <Typography variant="subtitle2">{product.name}</Typography>
          <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
            <Chip
              label={product.status}
              size="small"
              color={product.status === "active" ? "success" : "error"}
              variant="outlined"
            />
            <Typography variant="body2" color="text.secondary">
              ${product.base_price}
            </Typography>
            <Chip label={`${product.variants.length} variants`} size="small" />
          </Stack>
        </Box>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleOpenMenu(e, product.id);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      </Box>

      <Box onClick={(e) => e.stopPropagation()}>
        <Accordion sx={{ boxShadow: "none", "&:before": { display: "none" } }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ p: 0, "& .MuiAccordionSummary-content": { my: 1 } }}
          >
            <Typography variant="button" color="text.secondary" sx={{ pl: 2 }}>
              Product Details
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={1.5} sx={{ px: 2 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Brand:</Typography>
                <Typography variant="body2" color="text.secondary">
                  {product.brand || "N/A"}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Category:</Typography>
                <Typography variant="body2" color="text.secondary">
                  {product.category || "N/A"}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Master SKU:</Typography>
                <Typography variant="body2" color="text.secondary">
                  {product.master_sku}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Stock:</Typography>
                <Typography variant="body2" color="text.secondary">
                  {calculateTotalStock(product)}
                </Typography>
              </Stack>
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ boxShadow: "none", "&:before": { display: "none" } }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ p: 0, "& .MuiAccordionSummary-content": { my: 1 } }}
          >
            <Typography variant="button" color="text.secondary" sx={{ pl: 2 }}>
              Variants ({product.variants.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2} sx={{ px: 2 }}>
              {product.variants.map((variant, index) => (
                <Box key={variant.id}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      src={variant.image || product.image}
                      alt={variant.variant_name}
                      variant="square"
                      sx={{ width: 40, height: 40 }}
                    />
                    <Box flexGrow={1}>
                      <Typography variant="body2">
                        {variant.variant_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        SKU: {variant.sku}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="body2">${variant.price}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Stock: {variant.stock_quantity}
                      </Typography>
                    </Box>
                  </Stack>
                  {index < product.variants.length - 1 && <Divider sx={{ my: 1.5 }} />}
                </Box>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Paper>
  );

  const renderDesktopTable = () => {
    const TableHeader = () => (
      <TableHead>
        <TableRow>
          <TableCell>Image</TableCell>
          <TableCell>Name</TableCell>
          <TableCell>Brand</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Category</TableCell>
          <TableCell>SKU</TableCell>
          <TableCell align="right">Price</TableCell>
          <TableCell align="center">Stock</TableCell>
          <TableCell align="center">Variants</TableCell>
          <TableCell align="center">Actions</TableCell>
        </TableRow>
      </TableHead>
    );

    const ProductRow = ({ product }: { product: Product }) => (
      <React.Fragment key={product.id}>
        <TableRow hover onClick={() => navigate(`/dashboard/products/edit/${product.id}`)}>
          <TableCell>
            <Avatar
              src={product.image}
              alt={product.name}
              variant="square"
              sx={{ width: 50, height: 50 }}
            />
          </TableCell>
          <TableCell>{product.name}</TableCell>
          <TableCell>{product.brand || "N/A"}</TableCell>
          <TableCell>
            <Chip
              label={product.status}
              color={product.status === "active" ? "success" : "error"}
              size="small"
              variant="outlined"
            />
          </TableCell>
          <TableCell>{product.category || "N/A"}</TableCell>
          <TableCell>{product.master_sku}</TableCell>
          <TableCell align="right">${product.base_price}</TableCell>
          <TableCell align="center">{calculateTotalStock(product)}</TableCell>
          <TableCell align="center">{product.variants.length}</TableCell>
          <TableCell align="center">
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                handleOpenMenu(event, product.id);
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={10} sx={{ p: 0 }}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">View Variants</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Variant Name</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell align="right">Stock</TableCell>
                      <TableCell>Image</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {product.variants.map((variant) => (
                      <TableRow key={variant.id}>
                        <TableCell>{variant.variant_name}</TableCell>
                        <TableCell>{variant.sku}</TableCell>
                        <TableCell align="right">${variant.price}</TableCell>
                        <TableCell align="right">{variant.stock_quantity}</TableCell>
                        <TableCell>
                          <Avatar
                            src={variant.image || product.image}
                            alt={variant.variant_name}
                            variant="square"
                            sx={{ width: 40, height: 40 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );

    return (
      <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 300px)', overflow: 'auto' }}>
        <Table stickyHeader sx={{ minWidth: 800 }}>
          <TableHeader />
          <TableBody>
            {filteredProducts.map((product) => (
              <ProductRow key={product.id} product={product} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box p={isMobile ? 1 : 2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexDirection={isMobile ? "column" : "row"}
        gap={isMobile ? 2 : 0}
      >
        <Typography variant="h5">Product Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/dashboard/products/add")}
          fullWidth={isMobile}
        >
          Add Product
        </Button>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{ mb: 2 }}
      >
        <Tab label="Active" value="active" />
        <Tab label="Inactive" value="inactive" />
      </Tabs>

      <SearchFilterBar
        onSearch={handleSearch}
        onFilter={handleFilter}
        categories={getUniqueCategories()}
        brands={getUniqueBrands()}
      />

      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
        {isMobile ? (
          <Box sx={{ overflowX: "hidden", overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
            {filteredProducts.map(renderMobileProduct)}
          </Box>
        ) : (
          renderDesktopTable()
        )}
      </Box>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleEditProduct}>Edit</MenuItem>
        <MenuItem onClick={handleDeleteProduct}>Delete</MenuItem>
      </Menu>
    </Box>
  );
};

export default ProductManagementPage;